import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useConfirm } from 'hooks';
import i18n from 'i18n-js';
import moment from 'moment';
import Layout from 'components/Layout';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Title from 'components/Title';
import { ReceivableItemsTable, ReceivableCard, ReceivableItem } from 'components/Receivable';
import { formatAmount, getDynamicLink } from 'utils';
import { getSourceItemString } from 'utils/utils';
import { InvoiceActions } from 'state/modules/invoice';
import { InvoiceItemModal } from 'components/Modals';
import { FETCH_STATE } from 'constants';

const BillingPreview = () => {
  const { id } = useParams();

  const dispatch = useDispatch();
  const isMounted = useRef(false);

  // Component state
  const [openModal, setOpenModal] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  // Redux State
  const receivableType = useSelector((state) => state?.config?.settings?.core?.receivable?.type);
  const invoice = useSelector((state) => state.invoice.invoice);
  const loadingInvoice = useSelector((state) => state.invoice.loading);
  const fetchingInvoice = useSelector((state) => state.invoice.fetchingInvoice);
  const fetchingInvoiceItem = useSelector((state) => state.invoice.fetchingInvoiceItem);
  const localeFormat = useSelector((state) => state.organizations.localeFormat);

  const loading = loadingInvoice || fetchingInvoice === FETCH_STATE.LOADING;

  /**
   * Toggle confirm modal
   * @param {string} item Id of the item
   */
  const handleDeleteItem = (item) => {
    setCurrentItem(item);
    toggleDeleteItem();
  };

  /**
   * Toggle item modal
   * @param {item} item Current invoice item
   */
  const handleClickModal = (item) => {
    setCurrentItem(item);
    setOpenModal((prevValue) => !prevValue);
  };

  const [toggleDeleteItem, DeleteItemConfirm] = useConfirm({
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.delete'),
    title: i18n.t(`receivable.delete_${receivableType}_item`),
    message: i18n.t('receivable.delete_item_message'),
    onConfirm: useCallback(() => {
      dispatch(InvoiceActions.fetchDeleteItemFromInvoice(currentItem));
    }, [dispatch, currentItem]),
  });

  useEffect(() => {
    if (id && fetchingInvoice === FETCH_STATE.UNSENT && isMounted.current) {
      dispatch(InvoiceActions.fetchInvoiceById(id));
    }
  }, [dispatch, id, fetchingInvoice]);

  useEffect(() => {
    if (id) {
      dispatch(InvoiceActions.fetchInvoiceById(id));
      isMounted.current = true;
    }
  }, []);

  const { source, source_id } =
    invoice?.source_name === 'SubscriptionPeriod'
      ? {
          source: invoice?.extra_source_name,
          source_id: invoice?.extra_source_id,
        }
      : { source: invoice?.source_name, source_id: invoice?.source_id };

  const receivableInfoData = [
    {
      title: i18n.t('receivable.from'),
      description: invoice?.account_emitter?.customer_name,
    },
    {
      title: i18n.t('receivable.to'),
      description: (
        <Box component="span" sx={{ '& a': { fontWeight: 500, color: 'primary.main' } }}>
          {getDynamicLink(
            invoice?.account_customer?.customer_name,
            invoice?.account_customer?.holder_id,
            'Driver'
          )}
        </Box>
      ),
    },
    {
      title: i18n.t('receivable.source'),
      description: getDynamicLink(
        getSourceItemString(source, source_id),
        source_id,
        source,
        undefined,
        undefined,
        invoice?.description
      ),
    },
    {
      title: i18n.t('receivable.completed_on'),
      description:
        invoice?.completed_on && moment(invoice?.completed_on).isValid()
          ? moment(invoice?.completed_on).format(`${localeFormat}  hh:mm A`)
          : '-',
    },
    {
      title: i18n.t('receivable.modified_on'),
      description:
        invoice?.modified_time && moment(invoice?.modified_time).isValid()
          ? moment(invoice?.modified_time).format(`${localeFormat}  hh:mm A`)
          : '-',
    },
  ];

  const receivableTotalsData = [
    {
      label: i18n.t('receivable.total_billed'),
      value: '$' + formatAmount(invoice?.invoiced),
    },
  ];

  return (
    <Layout title={i18n.t('receivable.billing')}>
      <Container sx={{ minWidth: '100%', margin: 0 }}>
        <Grid container spacing={3} data-cy="receivable-preview">
          <Grid size={12}>
            <Grid container spacing={3}>
              <Grid style={{ marginRight: 'auto' }}>
                <Title title={i18n.t(`receivable.${receivableType}_details`, { id: id })} />
              </Grid>
            </Grid>
          </Grid>
          <Grid size={12}>
            <Paper
              elevation={2}
              sx={{ boxShadow: '0px 3px 6px #0000001A', padding: '50px 48px 32px' }}
            >
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography component="span">
                      <Box component="span" sx={{ fontSize: 20, fontWeight: 'fontWeightBold' }}>
                        {`${i18n.t(`receivable.${receivableType}`)} ID #${id}`}
                      </Box>
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={12}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      {receivableInfoData?.map(
                        (item) =>
                          !item.hide && (
                            <ReceivableItem
                              key={item.title}
                              title={item.title}
                              description={item.description}
                            />
                          )
                      )}
                    </Box>
                  </Box>
                </Grid>
                <Grid size={12}>
                  <Typography component="span">
                    <Box
                      component="span"
                      sx={{ mb: 2, fontSize: 13, fontWeight: 'fontWeightBold' }}
                    >
                      {i18n.t(`receivable.billed_items`)}
                    </Box>
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <ReceivableItemsTable
                    loading={loading}
                    handleEditItem={handleClickModal}
                    handleCreateItem={handleClickModal}
                    handleDeleteItem={handleDeleteItem}
                    receivable={invoice || {}}
                    showItemDetailsLink={true}
                  />
                </Grid>
                <Grid size={12}>
                  <Box sx={{ display: 'flex', mt: -5.5, justifyContent: 'flex-end' }}>
                    <ReceivableCard list={receivableTotalsData} />
                  </Box>
                </Grid>
              </Grid>
              <DeleteItemConfirm />
              {openModal ? (
                <InvoiceItemModal
                  invoice={id}
                  open={openModal}
                  current={currentItem}
                  loading={fetchingInvoiceItem}
                  handleClick={handleClickModal}
                />
              ) : null}
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export default BillingPreview;
